import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { INCIDENTE_TIPO, MOTIVO_PENDIENTE, faltaNombre } from "@/lib/constants";
import { formatHora, fechaHoraCompleta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ContainerId } from "@/components/marca/ContainerId";
import { PageHeader } from "@/components/marca/Layout";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { AlertOctagon, Clock, RefreshCw, ChevronRight, Loader2, CheckCircle2, FileWarning, PackageSearch, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export default function Alertas() {
  const navigate = useNavigate();
  const [data, setData] = useState({ incidentes_abiertos: [], pendientes_hoy: [], por_reasignar: [], papeles_observados: [], pedidos_pendientes: [], gastos_pendiente_aprobacion: [] });
  const [resolver, setResolver] = useState(null);

  const cargar = useCallback(async () => {
    try { const { data } = await api.get("/alertas"); setData(data); } catch { /* */ }
  }, []);

  usePolling(cargar, 15000);

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <PageHeader titulo={t("Alertas")} subtitulo={t("Lo que necesita atención de la oficina ahora")} />

      <Seccion titulo={t("Incidentes abiertos")} icon={AlertOctagon} color="text-danger" count={data.incidentes_abiertos.length}>
        {data.incidentes_abiertos.map((i) => (
          <div key={i.id} data-testid={`alerta-incidente-${i.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E8B9B8] border-l-4 border-l-danger bg-[#FBEDEC]">
            <button onClick={() => i.carga_id && navigate(`/panel/cargas/${i.carga_id}`)} className="text-left">
              <p className="font-semibold text-danger">{INCIDENTE_TIPO[i.tipo] || i.tipo} · {i.chofer_nombre}</p>
              <p className="text-xs text-ink-2 flex items-center gap-1.5 flex-wrap">{i.contenedor ? <ContainerId numero={i.contenedor} size="sm" /> : t("Sin carga")} · {fechaHoraCompleta(i.created_at)}{i.descripcion ? ` — ${i.descripcion}` : ""}</p>
            </button>
            <Button data-testid={`alerta-resolver-${i.id}`} size="sm" className="bg-danger hover:bg-[#A92825] text-white" onClick={() => setResolver(i)}>{t("Resolver")}</Button>
          </div>
        ))}
      </Seccion>

      <Seccion titulo={t("Pendientes informados (hoy)")} icon={Clock} color="text-[#B07A12]" count={data.pendientes_hoy.length}>
        {data.pendientes_hoy.map((p, idx) => (
          <button key={idx} data-testid="alerta-pendiente" onClick={() => p.carga_id && navigate(`/panel/cargas/${p.carga_id}`)} className="flex items-center justify-between w-full gap-3 p-3 rounded-lg border border-[#E9D9B0] bg-[#FBF5E6] text-left">
            <span className="text-sm text-ink flex items-center gap-2 flex-wrap"><ContainerId numero={p.contenedor || "—"} size="sm" />{t(" · {chofer_nombre} · Falta {v1} · {v2}", { chofer_nombre: p.chofer_nombre, v1: faltaNombre(p.falta), v2: MOTIVO_PENDIENTE[p.motivo] || p.motivo })}</span>
            <ChevronRight className="h-4 w-4 text-ink-3" />
          </button>
        ))}
      </Seccion>

      <Seccion titulo={t("Cargas por reasignar")} icon={RefreshCw} color="text-ink-2" count={data.por_reasignar.length}>
        {data.por_reasignar.map((c) => (
          <button key={c.carga_id} data-testid={`alerta-reasignar-${c.carga_id}`} onClick={() => navigate(`/panel/cargas/${c.carga_id}`)} className="flex items-center justify-between w-full gap-3 p-3 rounded-lg border border-line-strong bg-accent text-left">
            <span className="text-sm text-ink flex items-center gap-2 flex-wrap"><ContainerId numero={c.contenedor} size="sm" />{c.ubicacion ? t(" · quedó en {ubicacion}", { ubicacion: c.ubicacion }) : ""}</span>
            <ChevronRight className="h-4 w-4 text-ink-3" />
          </button>
        ))}
      </Seccion>

      <Seccion titulo={t("Papeles con observaciones")} icon={FileWarning} color="text-[#B07A12]" count={data.papeles_observados.length}>
        {data.papeles_observados.map((c) => (
          <button key={c.carga_id} data-testid={`alerta-papel-${c.carga_id}`} onClick={() => navigate(`/panel/cargas/${c.carga_id}`)} className="flex items-center justify-between w-full gap-3 p-3 rounded-lg border border-[#E9D9B0] bg-[#FBF5E6] text-left">
            <span className="text-sm text-ink flex items-center gap-2 flex-wrap"><ContainerId numero={c.contenedor} size="sm" />{t(" · papel subido con observaciones")}</span>
            <ChevronRight className="h-4 w-4 text-ink-3" />
          </button>
        ))}
      </Seccion>

      <Seccion titulo={t("Pedidos de insumos pendientes")} icon={PackageSearch} color="text-ink-2" count={data.pedidos_pendientes.length}>
        {data.pedidos_pendientes.map((p) => (
          <button key={p.id} data-testid={`alerta-pedido-${p.id}`} onClick={() => navigate("/panel/insumos")} className="flex items-center justify-between w-full gap-3 p-3 rounded-lg border border-line-strong bg-accent text-left">
            <span className="text-sm text-ink flex items-center gap-2 flex-wrap"><span className="font-semibold">{p.categoria_nombre}</span> · {p.chofer_nombre} · {p.cantidad}</span>
            <ChevronRight className="h-4 w-4 text-ink-3" />
          </button>
        ))}
      </Seccion>

      <Seccion titulo={t("Gastos pendientes de aprobación")} icon={BadgeDollarSign} color="text-ink-2" count={data.gastos_pendiente_aprobacion.length}>
        {data.gastos_pendiente_aprobacion.map((g) => (
          <button key={g.id} data-testid={`alerta-gasto-${g.id}`} onClick={() => navigate("/panel/insumos")} className="flex items-center justify-between w-full gap-3 p-3 rounded-lg border border-line-strong bg-accent text-left">
            <span className="text-sm text-ink flex items-center gap-2 flex-wrap"><span className="font-semibold">{g.categoria_nombre}</span> · {g.chofer_nombre} · ${g.monto?.toFixed(2)} — {g.descripcion}</span>
            <ChevronRight className="h-4 w-4 text-ink-3" />
          </button>
        ))}
      </Seccion>

      {resolver && <ResolverDialog incidente={resolver} onClose={() => setResolver(null)} onDone={() => { setResolver(null); cargar(); }} />}
    </div>
  );
}

function Seccion({ titulo, icon: Icon, color, count, children }) {
  return (
    <div className="bg-white rounded-xl border border-line p-4 lg:p-5">
      <h3 className="flex items-center gap-2 eyebrow mb-3">
        <Icon className={`h-4 w-4 ${color}`} /> {titulo} <span className="font-mono normal-case tracking-normal text-ink-2">{count}</span>
      </h3>
      {count === 0 ? <p className="text-sm text-ink-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#277A3F]" />{t(" Nada pendiente")}</p> : <div className="space-y-2">{children}</div>}
    </div>
  );
}

function ResolverDialog({ incidente, onClose, onDone }) {
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    setSaving(true);
    try {
      await api.post(`/incidentes/${incidente.id}/resolver`, { nota });
      toast.success(t("Incidente resuelto"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{t("Resolver incidente — {v0}", { v0: INCIDENTE_TIPO[incidente.tipo] })}</DialogTitle></DialogHeader>
        <Textarea data-testid="resolver-nota" placeholder={t("Nota de resolución (opcional)")} value={nota} onChange={(e) => setNota(e.target.value)} rows={3} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button data-testid="resolver-guardar"  onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Marcar resuelto")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
